import { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { Link } from "react-router-dom";

interface FAQItem {
  question: string;
  answer: string;
  category: string;
}

const MerchantProcessingFAQ = () => {
  const [openItems, setOpenItems] = useState<string[]>([]);

  const toggleItem = (question: string) => {
    setOpenItems(prev => 
      prev.includes(question) 
        ? prev.filter(item => item !== question)
        : [...prev, question]
    );
  };

  const faqData: FAQItem[] = [
    {
      category: "Cash Discount Program",
      question: "How does the cash discount program eliminate credit card processing fees?",
      answer: "Our compliant cash discount program allows you to pass processing costs to customers who pay with credit cards while offering discounts to cash-paying customers. This is 100% legal under the Dodd-Frank Act and follows all card network regulations. Instead of absorbing 3-4% in processing fees, you can eliminate these costs entirely while remaining fully compliant."
    },
    {
      category: "Cash Discount Program", 
      question: "Is the cash discount program legal and compliant?",
      answer: "Yes, absolutely! Our cash discount program is fully compliant with the Dodd-Frank Act, CFPB guidelines, and all major card network rules (Visa, Mastercard, Discover, American Express). We provide proper signage, disclosures, and training to ensure your business follows all regulations. Thousands of businesses nationwide use this legal method to eliminate processing fees."
    },
    {
      category: "Pricing & Fees",
      question: "What are your credit card processing rates and fees?",
      answer: "We offer the most competitive credit card processing rates in the industry. Our cash discount program eliminates processing fees entirely, while our traditional processing rates start at 2.6% + $0.10 per transaction. No setup fees, no cancellation fees, and no long-term contracts. We guarantee to beat your current processing rates or provide a cash discount solution that saves you money."
    },
    {
      category: "Pricing & Fees",
      question: "Are there any hidden fees or monthly minimums?",
      answer: "No hidden fees ever! We believe in transparent pricing. Our cash discount program only charges equipment rental fees. Traditional processing has no monthly minimums, no statement fees, and no cancellation penalties. All fees are clearly disclosed upfront with no surprises on your monthly statements."
    },
    {
      category: "Equipment & Setup",
      question: "What POS equipment and payment terminals do you provide?",
      answer: "We provide professional-grade payment terminals including Clover POS systems, traditional terminals, and mobile payment solutions. Affordable, high-performance equipment with competitive lease options and flexible payment plans. We support EMV chip readers, contactless payments, mobile wallets (Apple Pay, Google Pay), and integration with most existing POS systems."
    },
    {
      category: "Equipment & Setup",
      question: "How long does merchant account setup take?",
      answer: "Most applications are reviewed within 1-5 business days, depending on your industry and business type. Our streamlined application process takes just 5 minutes to complete. Once approved, premium equipment can be shipped quickly for professional setup. Our technical team provides installation, training, and ongoing support."
    },
    {
      category: "Business Types",
      question: "What types of businesses can use your merchant services?",
      answer: "We serve all business types including restaurants, retail stores, salons, medical offices, e-commerce, service providers, and high-risk merchants. Our solutions work for businesses processing $1,000 to $1,000,000+ monthly. We specialize in helping small to medium businesses save money on payment processing."
    },
    {
      category: "Support & Service",
      question: "What kind of customer support do you provide?",
      answer: "We provide 24/7 customer support with real people, not automated systems. Your dedicated account manager assists with setup, training, troubleshooting, and optimization. Equipment support, chargeback assistance, and ongoing merchant account management are included. Call (954) 399-2230 anytime for immediate assistance."
    },
    {
      category: "Comparison",
      question: "How do you compare to Square, Stripe, and other processors?",
      answer: "Unlike Square and Stripe who charge flat rates of 2.9-3.5%, we offer cash discount programs that eliminate fees entirely or provide interchange-plus pricing that's typically 40-60% lower. We provide dedicated support, reliable POS systems, and no volume restrictions. Our solutions are designed for businesses that want to maximize profits and minimize processing costs."
    },
    {
      category: "Chargeback & Security",
      question: "Do you help with chargeback prevention and fraud protection?",
      answer: "Yes! We provide comprehensive chargeback prevention tools, fraud monitoring, and dispute resolution assistance. Our EMV chip readers and tokenization technology reduce fraud risk by 95%. We help merchants understand chargeback reasons and implement prevention strategies to protect your business and maintain low chargeback ratios."
    }
  ];

  const categories = Array.from(new Set(faqData.map(item => item.category)));

  return (
    <section className="py-16 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50" aria-labelledby="faq-heading">
      <div className="container mx-auto px-4">
        <header className="text-center mb-12">
          <h2 id="faq-heading" className="text-3xl font-bold mb-4 text-gray-800">
            Frequently Asked Questions About Credit Card Processing & Merchant Services
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Get answers to common questions about our cash discount programs, credit card processing rates, 
            POS equipment, and merchant services. Can't find what you're looking for? Call us at (954) 399-2230.
          </p>
        </header>

        <div className="max-w-4xl mx-auto">
          {categories.map((category) => (
            <div key={category} className="mb-8">
              <h3 className="text-xl font-semibold mb-4 text-blue-800 border-b border-blue-200 pb-2">
                {category}
              </h3>
              <div className="space-y-4">
                {faqData
                  .filter(item => item.category === category)
                  .map((faq) => (
                    <div
                      key={faq.question}
                      className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden"
                    >
                      <button
                        onClick={() => toggleItem(faq.question)}
                        className="w-full text-left p-6 hover:bg-gray-50 transition-colors duration-200 flex items-center justify-between"
                        aria-expanded={openItems.includes(faq.question)}
                      >
                        <h4 className="text-lg font-semibold text-gray-800 pr-4">
                          {faq.question}
                        </h4>
                        {openItems.includes(faq.question) ? (
                          <ChevronUp className="h-5 w-5 text-blue-600 flex-shrink-0" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-blue-600 flex-shrink-0" />
                        )}
                      </button>
                      {openItems.includes(faq.question) && (
                        <div className="px-6 pb-6">
                          <p className="text-gray-700 leading-relaxed">
                            {faq.answer}
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            </div>
          ))}
        </div>

        {/* Contact CTA */}
        <div className="text-center mt-12 bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
          <h3 className="text-2xl font-semibold mb-4 text-gray-800">
            Still Have Questions About Payment Processing?
          </h3>
          <p className="text-gray-600 mb-6">
            Our payment processing experts are standing by to help you choose the best merchant services solution for your business.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:9543992230"
              className="inline-flex items-center justify-center bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
            >
              Call (954) 399-2230 - Free Consultation
            </a>
            <Link
              to="/merchant-application"
              className="inline-flex items-center justify-center bg-green-700 text-white px-8 py-3 rounded-lg font-semibold hover:bg-green-800 transition-colors"
            >
              Apply Now - Fast Approval Process
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MerchantProcessingFAQ;